import { useAuth } from '@/components/auth/authProvider';
import { isPalmettoFinanceUser } from '@/components/auth/RequirePalmettoFinanceUser';
import { AsyncSelectInput } from '@/components/Forms/AsyncSelect';
import FormSection from '@/components/Forms/FormSection';
import { FlagBox } from '@/components/QueueFlags/FlagBox';
import { debounceAsync } from '@/helpers/debounceAsync';
import usePermissions from '@/hooks/usePermissions';
import { useGetHvacEquipmentCatalogQuery } from '@/services/equipmentCatalog';
import { useSearchProductsMutation } from '@/services/productCatalog';
import { Box, Button, FormikTextInput, SimulatedEventPayloadType, toast } from '@palmetto/palmetto-components';
import { Field, FieldArray, useFormikContext } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import {
  HVACAccessoryType,
  HVACEquipmentItem,
  HVACEquipmentType,
  HVACEquipmentTypeLabels,
  HVACManufacturer,
  RequirementFlagSettingsType,
  UserPermissions,
} from 'types';

interface EquipmentSectionProps {
  systemIndex: number;
  isPackageEditable?: boolean;
}

/*
 * The pre-AVL warranty URLs, kept as the flag-off path so turning the flag off reproduces today's
 * behaviour exactly. With the flag on these are replaced by each brand record's
 * `warrantyRegistrationUrl`, which disagrees with this list for 11 of the 18 manufacturers.
 */
const getLinkForManufacturer = (manufacturer: HVACManufacturer) => {
  switch (manufacturer) {
    case HVACManufacturer.trane:
      return 'https://www.trane.com/residential/en/resources/warranty-and-registration/register/';
    case HVACManufacturer.americanStandard:
    case HVACManufacturer.runTru:
      return 'https://www.americanstandardair.com/resources/warranty-and-registration/register/';
    case HVACManufacturer.ameristar:
      return 'https://warrantyregistration.tranetechnologies.com/wrApp/index.html#/ameristar/welcome';
    case HVACManufacturer.daikin:
      return 'https://daikincomfort.com/my-daikin-systems/product-registration';
    case HVACManufacturer.goodman:
      return 'https://www.goodmanmfg.com/product-registration';
    case HVACManufacturer.amana:
      return 'https://www.amana-hac.com/product-registration';
    case HVACManufacturer.samsung:
      return 'https://www.samsunghvac.com/register-product';
    case HVACManufacturer.mitsubishiElectric:
      return 'https://registermehvac.com/start.aspx';
    case HVACManufacturer.lennox:
      return 'https://www.warrantyyourway.com/';
    case HVACManufacturer.carrier:
      return 'https://productregistration.carrier.com/Public/Home?brand=carrier';
    case HVACManufacturer.bryant:
      return 'https://productregistration.bryant.com/Public/Home?brand=bryant';
    case HVACManufacturer.rheem:
      return 'https://rheem.registermyunit.com/v3/brand.html?brand=rheem';
    case HVACManufacturer.fujitsu:
      return 'https://access.fujitsugeneral.com/en-US/warranty/register';
    case HVACManufacturer.bosch:
      return 'https://bosch-us-home.thernovo.com/home';
    case HVACManufacturer.york:
      return 'https://www.york.com/residential-equipment/warranty-and-registration';
    case HVACManufacturer.lg:
      return 'https://lg-dfs-warranty.com/register';
    case HVACManufacturer.gree:
      return 'https://www.greecomfort.com/warranty-program/#registration';
    default:
      return '';
  }
};

const dataKey = 'equipment' as const;
const label = 'Equipment & Materials' as const;

export const InstallPackageEquipmentSection = ({ systemIndex, isPackageEditable = true }: EquipmentSectionProps) => {
  const permissions = usePermissions();
  const { claims } = useAuth();
  const isFinCoUser = isPalmettoFinanceUser(claims);
  const { enableAvlSystemManufacturerSelect } = useFlags();
  // The catalog endpoint 404s unless the flag is on and the caller is a FinCo user, so the query
  // has to be skipped rather than merely unused in those cases.
  const useAvlWarrantyUrls = !!enableAvlSystemManufacturerSelect && isFinCoUser;
  const { data: equipmentCatalog } = useGetHvacEquipmentCatalogQuery(undefined, { skip: !useAvlWarrantyUrls });
  /*
   * Each equipment item stores its manufacturer as a product-catalog string, so the warranty URL
   * comes from matching that string against the seeded brand names. Brand names are unique per
   * manufacturer but not across them, so a collision keeps the first brand in catalog order and
   * ignores later ones, matching how the server's own directory indexes them.
   */
  const warrantyUrlsByBrandName = useMemo(() => {
    const urls = new Map<string, string>();
    for (const manufacturer of equipmentCatalog?.manufacturers ?? []) {
      for (const brand of manufacturer.brands) {
        const key = brand.name.trim().toLowerCase();
        if (brand.warrantyRegistrationUrl && !urls.has(key)) {
          urls.set(key, brand.warrantyRegistrationUrl);
        }
      }
    }
    return urls;
  }, [equipmentCatalog]);
  const getWarrantyUrl = (manufacturer?: string) => {
    if (!manufacturer) return undefined;
    if (!useAvlWarrantyUrls) return getLinkForManufacturer(manufacturer as HVACManufacturer) || undefined;
    return warrantyUrlsByBrandName.get(manufacturer.trim().toLowerCase());
  };
  const userOnlyHasReaderPermission = permissions?.every((permission) =>
    permission.includes(UserPermissions.reader),
  );
  const { values, setFieldValue, handleBlur } = useFormikContext<any>();

  const [triggerSearch] = useSearchProductsMutation();

  const handleSearchText = async (searchText: string, productType: string) => {
    const { data, error } = await triggerSearch({
      ...(searchText ? { query: searchText } : {}),
      limit: 20,
      page: 1,
      filters: [
        {
          filter: 'attribute',
          key: 'lightreach-product-type',
          operation: {
            equals: productType,
          },
        },
        {
          filter: 'availableOnDate',
          operation: {
            equals: new Date().toISOString(),
          },
        },
      ],
    });
    if (error) {
      toast.error('Error searching products. Please try again.');
      return undefined;
    }
    const productData = data?.data?.map((product) => {
      const model = product.attributes.find((attr) => attr.key === 'model-number')?.value ?? '';
      const productType =
        product.attributes.find((attr) => attr.key === 'lightreach-product-type')?.value ??
        ('' as HVACEquipmentType);
      const isSerialNumberRequired =
        product.attributes.find((attr) => attr.key === 'lightreach-serial-number-required')?.value ?? false;
      return {
        label: `${product.manufacturer} - ${model}`,
        value: product.id,
        manufacturer: product.manufacturer,
        model,
        productType,
        name: product.name,
        isSerialNumberRequired,
      };
    });
    return productData?.length && (data?.paging?.totalPages || 0) > 1
      ? [
          {
            label: `${data?.paging.totalRecords} results found. Continue typing to narrow down results.`,
            value: undefined,
          },
          ...productData,
        ]
      : productData;
  };

  const debouncedSearch = debounceAsync(
    (value: string, equipmentType: HVACEquipmentType | HVACAccessoryType) => handleSearchText(value, equipmentType),
    400,
    2,
    true,
  );

  return (
    <FormSection
      title={label}
      description={`Update applicable ${label} for the proposal.`}
      baseStyle={{
        borderWidth: '0',
      }}
    >
      <FieldArray
        name={`systems.${systemIndex}[${dataKey}].items`}
        render={() => {
          return (
            <Box gap="lg">
              <Box direction={{ base: 'column', tablet: 'row' }} gap="md"></Box>
              {values?.systems[systemIndex]?.[dataKey]?.items?.map?.(
                (
                  equipmentAccessory: HVACEquipmentItem & {
                    isSerialNumberRequired?: boolean;
                    isOther?: boolean;
                  },
                  index: number,
                ) => {
                  const warrantyUrl = getWarrantyUrl(equipmentAccessory.manufacturer);
                  return (
                    <Box key={`system-${systemIndex}-${dataKey}-${equipmentAccessory.type}-${index}`} childGap="md">
                      <Box
                        key={`system-${systemIndex}-${dataKey}-${equipmentAccessory.type}-${index}`}
                        direction="row"
                        gap="lg"
                        alignItems="baseline"
                      >
                        <Box direction="column" gap="sm" width={{ base: '60', tablet: '40' }}>
                          <Box fontSize="md" fontWeight="medium">
                            {HVACEquipmentTypeLabels[equipmentAccessory.type]}
                          </Box>
                          {equipmentAccessory?.size || equipmentAccessory?.efficiencies ? (
                            <Box gap="xs" direction="column" fontSize="sm">
                              <Box>
                                Size: {equipmentAccessory?.size?.value} {equipmentAccessory?.size?.unit}
                              </Box>
                              <Box>
                                Efficiency:{' '}
                                {equipmentAccessory.efficiencies
                                  ?.map((efficiency) => `${efficiency.value} ${efficiency.unit}`)
                                  .join(', ')}
                              </Box>
                            </Box>
                          ) : null}
                          {equipmentAccessory.isOther ? (
                            <Box childGap="sm">
                              <Field
                                label={'Manufacturer'}
                                name={`systems.${systemIndex}[${dataKey}].items.${index}.manufacturer`}
                                id={`systems.${systemIndex}[${dataKey}].items.${index}.manufacturer`}
                                component={FormikTextInput}
                                isRequired
                                isDisabled={!isPackageEditable}
                              />
                              <Field
                                label={'Model'}
                                name={`systems.${systemIndex}[${dataKey}].items.${index}.model`}
                                id={`systems.${systemIndex}[${dataKey}].items.${index}.model`}
                                component={FormikTextInput}
                                isDisabled={!isPackageEditable}
                                isRequired
                              />
                            </Box>
                          ) : null}
                        </Box>
                        <Box
                          childGap="md"
                          width="100%"
                          direction="column"
                          padding={equipmentAccessory.isOther ? 'xl 0 0 0' : '0'}
                        >
                          {(equipmentAccessory?.manufacturer || equipmentAccessory?.model) &&
                          !equipmentAccessory.isOther ? (
                            <Box gap="sm" direction="row" alignItems="center">
                              <Box gap="xs" direction="column" fontSize="sm">
                                <Box fontSize="sm">
                                  {equipmentAccessory.manufacturer} - {equipmentAccessory.model}
                                  {equipmentAccessory.productId === 'other' ? '*' : ''}
                                </Box>
                                {equipmentAccessory?.name && equipmentAccessory?.productId !== 'other' && (
                                  <Box fontSize="sm" style={{ fontStyle: 'italic' }}>
                                    ({equipmentAccessory.name})
                                  </Box>
                                )}
                                {isFinCoUser && warrantyUrl && (
                                  <Box>
                                    <Link target="_blank" rel="noopener noreferrer" to={warrantyUrl}>
                                      <Button size="sm" variant="secondary">
                                        Warranty Registration
                                      </Button>
                                    </Link>
                                  </Box>
                                )}
                              </Box>
                              <Button
                                iconPrefix="remove"
                                variant="tertiary"
                                tone="danger"
                                aria-label="Clear model number"
                                size="sm"
                                isDisabled={!isPackageEditable}
                                onClick={() => {
                                  setFieldValue(
                                    `systems.${systemIndex}[${dataKey}].items.${index}.isOther`,
                                    undefined,
                                  );
                                  setFieldValue(
                                    `systems.${systemIndex}[${dataKey}].items.${index}.manufacturer`,
                                    undefined,
                                  );
                                  setFieldValue(
                                    `systems.${systemIndex}[${dataKey}].items.${index}.model`,
                                    undefined,
                                  );
                                  setFieldValue(
                                    `systems.${systemIndex}[${dataKey}].items.${index}.productId`,
                                    undefined,
                                  );
                                  setFieldValue(`systems.${systemIndex}[${dataKey}].items.${index}.name`, undefined);
                                }}
                              >
                                Clear
                              </Button>
                            </Box>
                          ) : (
                            <AsyncSelectInput
                              label={`Add ${HVACEquipmentTypeLabels[equipmentAccessory.type]} Model`}
                              id={`systems.${systemIndex}[${dataKey}].items.selectedItem`}
                              name={`systems.${systemIndex}[${dataKey}].items.selectedItem`}
                              loadOnOpen={!!equipmentAccessory.type}
                              loadOptions={async (input: string) => {
                                const label = HVACEquipmentTypeLabels[equipmentAccessory.type];
                                const options = await debouncedSearch(input, equipmentAccessory.type);
                                return [
                                  ...options,
                                  {
                                    label: (
                                      <Button
                                        as="button"
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => {
                                          setFieldValue(
                                            `systems.${systemIndex}[${dataKey}].items.${index}.isOther`,
                                            true,
                                          );
                                          setFieldValue(
                                            `systems.${systemIndex}[${dataKey}].items.${index}.manufacturer`,
                                            '',
                                          );
                                          setFieldValue(
                                            `systems.${systemIndex}[${dataKey}].items.${index}.model`,
                                            '',
                                          );
                                          setFieldValue(
                                            `systems.${systemIndex}[${dataKey}].items.${index}.productId`,
                                            'other',
                                          );
                                          setFieldValue(
                                            `systems.${systemIndex}[${dataKey}].items.${index}.name`,
                                            '',
                                          );
                                          setFieldValue(`systems.${systemIndex}[${dataKey}].equipmentType`, null);
                                        }}
                                      >
                                        Enter {label} not found in search
                                      </Button>
                                    ),
                                    value: 'other',
                                  },
                                ];
                              }}
                              isDisabled={userOnlyHasReaderPermission || !isPackageEditable}
                              onChange={(event) => {
                                if (event?.target?.value && event.target.value.value) {
                                  setFieldValue(`systems.${systemIndex}[${dataKey}].items.${index}`, {
                                    ...values.systems[systemIndex][dataKey].items[index],
                                    manufacturer: event?.target?.value.manufacturer,
                                    model: event?.target?.value.model,
                                    quantity: 1,
                                    productId: event?.target?.value.value,
                                    name: event?.target?.value.name,
                                    isOther: event?.target?.value.value === 'other',
                                  });
                                }
                              }}
                              onBlur={handleBlur}
                              isClearable
                              value={''}
                              placeholder="Search by Model or Name..."
                              noOptionsMessage={({ inputValue }: { inputValue: string | React.JSX.Element }) => {
                                const equipmentLabel = HVACEquipmentTypeLabels[equipmentAccessory.type];
                                if (!inputValue) return `Type to search for ${equipmentLabel}...`;
                                return (
                                  <Box display="flex" direction="column" gap="sm">
                                    <div>No {equipmentLabel} found</div>
                                    <Button
                                      as="button"
                                      variant="secondary"
                                      size="sm"
                                      onClick={() => {
                                        setFieldValue(
                                          `systems.${systemIndex}[${dataKey}].items.${index}.isOther`,
                                          true,
                                        );
                                        setFieldValue(
                                          `systems.${systemIndex}[${dataKey}].items.${index}.manufacturer`,
                                          '',
                                        );
                                        setFieldValue(`systems.${systemIndex}[${dataKey}].items.${index}.model`, '');
                                        setFieldValue(
                                          `systems.${systemIndex}[${dataKey}].items.${index}.productId`,
                                          'other',
                                        );
                                        setFieldValue(`systems.${systemIndex}[${dataKey}].items.${index}.name`, '');
                                        setFieldValue(`systems.${systemIndex}[${dataKey}].equipmentType`, null);
                                      }}
                                    >
                                      Enter {equipmentLabel} not found in search
                                    </Button>
                                  </Box>
                                );
                              }}
                            />
                          )}
                          {equipmentAccessory.type !== 'other' ? (
                            <>
                              <Box padding="0 0 xs 0">
                                {`Serial Number${equipmentAccessory?.isSerialNumberRequired ? ' *' : ''}`}
                              </Box>
                              <FieldArray
                                name={`systems.${systemIndex}[${dataKey}].items.${index}.serialNumbers`}
                                render={() => {
                                  return values?.systems?.[systemIndex]?.[dataKey]?.items?.[
                                    index
                                  ]?.serialNumbers?.map((_serial: string, serialIndex: number) => {
                                    return (
                                      <Box padding="0 0 sm 0" key={serialIndex}>
                                        <Field
                                          placeholder={'Serial Number'}
                                          name={`systems.${systemIndex}[${dataKey}].items.${index}.serialNumbers.${serialIndex}`}
                                          id={`systems.${systemIndex}[${dataKey}].items.${index}.serialNumbers.${serialIndex}`}
                                          component={FormikTextInput}
                                          width="100"
                                          isDisabled={userOnlyHasReaderPermission || !isPackageEditable}
                                          onChange={(event: SimulatedEventPayloadType) => {
                                            const serialNumbers = [
                                              ...(values.systems[systemIndex][dataKey]?.items[index]
                                                ?.serialNumbers || []),
                                            ];
                                            serialNumbers[serialIndex] = event?.target?.value.trim();
                                            setFieldValue(
                                              `systems.${systemIndex}[${dataKey}].items.${index}.serialNumbers`,
                                              serialNumbers,
                                            );
                                          }}
                                        />
                                      </Box>
                                    );
                                  });
                                }}
                              />
                            </>
                          ) : (
                            <Box width={{ base: '50', tablet: '20' }}></Box>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  );
                },
              )}

              <FlagBox
                baseCategory="equipment"
                dataKey={`Installed ${label} Issue ${systemIndex + 1}`}
                flagType={RequirementFlagSettingsType.equipmentHVAC}
              />
            </Box>
          );
        }}
      />
    </FormSection>
  );
};
